import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const BASE_URL = process.env.REACT_APP_BASE_URL || '';

const usePgmRateTablesFunctions = () => {
  const [table, setTable]         = useState(null);
  const [rates, setRates]         = useState([]);
  const [constants, setConstants] = useState({});
  // WHICH waiting periods are actually offered, as opposed to which ones
  // the signed tariff prices. A commercial choice, so it is the tenant's
  // — this is the setting AROMED needs for tracker R35 and it was
  // previously stored but unreachable from any screen.
  const [offeredWaits, setOfferedWaits] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);
  const [dirty, setDirty]         = useState(false);
  const [error, setError]         = useState('');
  const [toast, setToast]         = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const resp = await axios.get(`${BASE_URL}/offers/pgm/rate-table`, { withCredentials: true });
      const data = resp.data?.data;
      setTable(data);
      setRates((data?.rates || []).map(r => ({ ...r })));
      setConstants({ ...(data?.constants || {}) });
      setOfferedWaits((data?.availableWaitingPeriods || []).map(Number));
      setDirty(false);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load PGM rate table');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const updateRate = useCallback((waitingPeriod, coveragePercent, field, value) => {
    setRates(prev => prev.map(r =>
      Number(r.waitingPeriod) === Number(waitingPeriod) && Number(r.coveragePercent) === Number(coveragePercent)
        ? { ...r, [field]: value }
        : r
    ));
    setDirty(true);
  }, []);

  const updateConstant = useCallback((field, value) => {
    setConstants(prev => ({ ...prev, [field]: value }));
    setDirty(true);
  }, []);

  const save = useCallback(async () => {
    setSaving(true);
    setError('');
    try {
      const ratesToSend = rates.map(r => ({
        waitingPeriod: Number(r.waitingPeriod),
        coveragePercent: Number(r.coveragePercent),
        rate: Number(r.rate),
        confirmed: Boolean(r.confirmed),
      }));
      const constantsToSend = {
        salaryCap: Number(constants.salaryCap),
        maxBenefitDays: Number(constants.maxBenefitDays),
        avsBenefitDays: Number(constants.avsBenefitDays),
        minimumPremium: Number(constants.minimumPremium),
        currency: constants.currency,
        defaultNogaCode: constants.defaultNogaCode,
        coverageDescription: constants.coverageDescription,
      };
      const resp = await axios.put(
        `${BASE_URL}/offers/pgm/rate-table`,
        {
          rates: ratesToSend,
          constants: constantsToSend,
          availableWaitingPeriods: [...offeredWaits].sort((a, b) => a - b),
        },
        { withCredentials: true },
      );
      const data = resp.data?.data;
      setTable(data);
      setRates((data?.rates || []).map(r => ({ ...r })));
      setConstants({ ...(data?.constants || {}) });
      setOfferedWaits((data?.availableWaitingPeriods || []).map(Number));
      setDirty(false);
      setToast('PGM rate table saved');
      setTimeout(() => setToast(''), 2400);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save PGM rate table');
    } finally {
      setSaving(false);
    }
  }, [rates, constants, offeredWaits]);

  // Refuses to leave the product unsellable: at least one period must stay
  // on offer, and the backend re-checks that every offered period has a
  // priced row behind it.
  const toggleOfferedWait = useCallback((wp) => {
    setOfferedWaits((prev) => {
      const n = Number(wp);
      const next = prev.includes(n) ? prev.filter((x) => x !== n) : [...prev, n];
      return next.length ? next.sort((a, b) => a - b) : prev;
    });
    setDirty(true);
  }, []);

  return {
    table,
    rates,
    constants,
    offeredWaits,
    toggleOfferedWait,
    updateRate, updateConstant,
    loading, saving, dirty, error, toast,
    save,
    reload: load,
  };
};

export default usePgmRateTablesFunctions;
